
import type React from 'react';

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { SendIcon, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import {
  useChatMessages,
  useSendMessage,
  useMarkRead,
} from '@/api/chat';

interface ChatConversationProps {
  conversationId: string;
}

export function ChatConversation({ conversationId }: ChatConversationProps) {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { data, isLoading } = useChatMessages(conversationId);
  const sendMutation = useSendMessage();
  const markReadMutation = useMarkRead();

  const messages = data?.messages ?? [];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (conversationId) {
      markReadMutation.mutate(conversationId);
    }
  }, [conversationId]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || sendMutation.isPending) return;

    sendMutation.mutate(
      { conversationId, content: message.trim() },
      { onSuccess: () => setMessage('') },
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <ScrollArea className="min-h-0 flex-1 pr-4">
        <div className="space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-8 text-sm text-muted-foreground">
              No messages yet. Start the conversation!
            </div>
          )}
          {messages.map((msg) => (
            <div
              key={msg._id}
              className={cn(
                'flex items-start gap-3',
                msg.senderType === 'owner' ? 'flex-row-reverse' : 'flex-row'
              )}
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback>
                  {msg.senderType === 'owner' ? 'O' : 'U'}
                </AvatarFallback>
              </Avatar>
              <div
                className={cn(
                  'rounded-lg px-4 py-2 max-w-[80%]',
                  msg.senderType === 'owner'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                )}
              >
                <p className="whitespace-pre-wrap">{msg.content}</p>
                <p className="text-xs opacity-70 mt-1">
                  {format(new Date(msg.createdAt), 'p')}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>

      <form
        onSubmit={handleSendMessage}
        className="flex items-center gap-2 mt-4 pt-4 border-t"
      >
        <Input
          placeholder={t('chat.typeMessage')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          className="flex-1"
        />
        <Button
          type="submit"
          size="icon"
          disabled={sendMutation.isPending || !message.trim()}
        >
          {sendMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <SendIcon className="h-4 w-4" />
          )}
          <span className="sr-only">{t('chat.send')}</span>
        </Button>
      </form>
    </div>
  );
}
